import { MapPin, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useMineMarkedsomrader } from '../hooks/useMineMarkedsomrader'
import { useActiveMarkedsomrade } from '../hooks/useActiveMarkedsomrade'

export type PostnummerDirection = 'bilkjøper' | 'bilselger' | 'begge'

interface PostnummerFilterProps {
  enabled: boolean
  onToggle: (enabled: boolean) => void
  compact?: boolean
  direction?: PostnummerDirection
  onDirectionChange?: (d: PostnummerDirection) => void
  /**
   * Controlled markedsområde selection (#526).
   *
   * By default the dropdown writes straight to the localStorage-backed
   * `useActiveMarkedsomrade`, which `useMinePostnummer` reads — so choosing an
   * område immediately re-runs every query on the page. Pass these two to hold
   * the choice as a draft instead and commit it yourself. Omit them and the
   * component behaves exactly as before, so other modules are unaffected.
   */
  omradeId?: number | null
  onOmradeChange?: (id: number | null) => void
}

export default function PostnummerFilter({ enabled, onToggle, compact = false, direction = 'bilkjøper', onDirectionChange, omradeId, onOmradeChange }: PostnummerFilterProps) {
  const { t } = useTranslation('components')
  const { user, loading: authLoading } = useAuth()
  const { items: omrader, loading: omraderLoading } = useMineMarkedsomrader()
  const { activeId, setActiveId, clearActive } = useActiveMarkedsomrade()

  const controlled = onOmradeChange !== undefined
  const selectedId = controlled ? (omradeId ?? null) : activeId
  const activeOmrade = selectedId !== null ? omrader.find(o => o.id === selectedId) : undefined
  const postnummer =
    activeOmrade ? activeOmrade.postnumre.join(',') : (user?.mine_postnummer ?? null)
  const loading = authLoading && !user

  // Don't show the filter if user has no postal codes
  if (!postnummer && !loading) {
    return null
  }

  const handleToggle = () => {
    onToggle(!enabled)
  }

  return (
    <div className={compact ? '' : 'card p-4'}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${enabled ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
            <MapPin className={`w-5 h-5 ${enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {t('postnummerFilter.title')}
              </span>
              {loading && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('postnummerFilter.loading')}</span>
              )}
            </div>

            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {omrader.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">{t('postnummerFilter.area')}</label>
                  <select
                    value={selectedId ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      const next = v === '' ? null : Number.parseInt(v, 10)
                      if (controlled) {
                        onOmradeChange(next)
                      } else if (next === null) {
                        clearActive()
                      } else {
                        setActiveId(next)
                      }
                    }}
                    disabled={omraderLoading || !enabled}
                    className="text-xs px-2 py-1 border border-gray-300 dark:border-dark-border rounded-sm bg-white dark:bg-dark-card"
                  >
                    <option value="">{t('postnummerFilter.standardOption', { what: user?.mine_postnummer ? t('postnummerFilter.postnrCount', { n: user.mine_postnummer.split(',').length }) : t('postnummerFilter.none') })}</option>
                    {omrader.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name} ({o.postnumre.length})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {onDirectionChange && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">{t('postnummerFilter.filterOn')}</label>
                  <select
                    value={direction}
                    onChange={(e) => onDirectionChange(e.target.value as PostnummerDirection)}
                    disabled={!enabled}
                    className="text-xs px-2 py-1 border border-gray-300 dark:border-dark-border rounded-sm bg-white dark:bg-dark-card"
                  >
                    <option value="bilkjøper">{t('postnummerFilter.buyer')}</option>
                    <option value="bilselger">{t('postnummerFilter.seller')}</option>
                    <option value="begge">{t('postnummerFilter.both')}</option>
                  </select>
                </div>
              )}
            </div>

            {postnummer ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {postnummer.split(',').slice(0, 5).map((code, index) => (
                  <span
                    key={index}
                    className="text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-sm"
                  >
                    {code.trim()}
                  </span>
                ))}
                {postnummer.split(',').length > 5 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('postnummerFilter.more', { n: postnummer.split(',').length - 5 })}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('postnummerFilter.noPostnummer')}
              </p>
            )}
          </div>
        </div>

        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={enabled}
              onChange={handleToggle}
              disabled={!postnummer || loading}
              className="sr-only"
            />
            <div className={`block w-14 h-8 rounded-full transition ${
              enabled
                ? 'bg-green-500 dark:bg-green-600'
                : 'bg-gray-300 dark:bg-gray-600'
            } ${(!postnummer || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            </div>
            <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
              enabled ? 'transform translate-x-6' : ''
            }`}>
            </div>
          </div>
          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
            {enabled ? t('postnummerFilter.enabled') : t('postnummerFilter.disabled')}
          </span>
        </label>
      </div>

      {enabled && postnummer && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <MapPin className="w-4 h-4" />
            <span>
              {t('postnummerFilter.filteringOn', { n: postnummer.split(',').length })}
            </span>
            <button
              onClick={() => onToggle(false)}
              className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title={t('postnummerFilter.disableFilter')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
